import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CashfreeReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const orderId = params.get("order_id") || "";
  const [state, setState] = useState({ loading: true, paid: false, status: "" });

  useEffect(() => {
    if (!orderId) { setState({ loading: false, paid: false, status: "MISSING_ID" }); return; }
    let stop = false;
    const check = async (retries = 6) => {
      try {
        const { data } = await api.get(`/payments/cashfree/status/${orderId}`);
        if (stop) return;
        if (data.paid) {
          setState({ loading: false, paid: true, status: data.status });
          // Guest-apply flow: transfer sessionStorage temp password to success page
          const stash = sessionStorage.getItem(`w2e_apply_${orderId}`);
          if (stash) {
            sessionStorage.removeItem(`w2e_apply_${orderId}`);
            nav(`/apply/success/${orderId}`, { state: JSON.parse(stash), replace: true });
          } else {
            setTimeout(() => nav(`/dashboard/orders/${orderId}`, { replace: true }), 1500);
          }
          return;
        }
        if (retries > 0) setTimeout(() => check(retries - 1), 2000);
        else setState({ loading: false, paid: false, status: data.status || "UNKNOWN" });
      } catch (e) {
        if (retries > 0) setTimeout(() => check(retries - 1), 2000);
        else setState({ loading: false, paid: false, status: "ERROR" });
      }
    };
    check();
    return () => { stop = true; };
  }, [orderId, nav]);

  return (
    <PublicLayout>
      <section className="py-24 gradient-radial-blue">
        <div className="max-w-md mx-auto text-center px-4" data-testid="cashfree-return">
          {state.loading ? (
            <>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#1E3A8A]" />
              <h1 className="mt-6 font-display font-bold text-2xl text-[#0A192F]">Verifying your payment…</h1>
              <p className="text-sm text-slate-600 mt-2">Please wait while we confirm with Cashfree. Do not refresh this page.</p>
            </>
          ) : state.paid ? (
            <>
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
              <h1 className="mt-6 font-display font-bold text-2xl text-[#0A192F]">Payment successful</h1>
              <p className="text-sm text-slate-600 mt-2">Redirecting you to your order…</p>
            </>
          ) : (
            <>
              <XCircle className="w-14 h-14 mx-auto text-red-500" />
              <h1 className="mt-6 font-display font-bold text-2xl text-[#0A192F]">Payment {state.status || "not confirmed"}</h1>
              <p className="text-sm text-slate-600 mt-2">If you were charged, contact <a href="mailto:support@way2efiling.com" className="text-[#1E3A8A] underline">support@way2efiling.com</a> with the reference above.</p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/dashboard" className="px-4 py-2 rounded-md bg-[#1E3A8A] text-white text-sm">Go to Dashboard</Link>
                <Link to="/services" className="px-4 py-2 rounded-md border border-slate-300 text-sm">Try again</Link>
              </div>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
